// src/pages/public/Gallery.tsx
// Photo gallery — paginated, with prev/next lightbox navigation.
// Sign-in required. Anonymous visitors see a sign-in CTA.
//
// Three image sizes (since photos are big):
//   - thumbnailUrl (~800x600 q82) → gallery grid
//   - mediumUrl    (~1280x960 q85) → lightbox default view
//   - url          (full size)     → "View Full Size" button (new tab)
// Neighbors preload eagerly when the lightbox opens, so ‹/› feels instant.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import PhotoUpload from '../../components/upload/PhotoUpload';

interface Photo {
  id: string;
  url: string;
  caption: string | null;
  tags: string[];
  takenAt?: string;
  uploadedAt?: string;
  credit: string | null;
  thumbnailUrl?: string;
  mediumUrl?: string;
}

interface PhotosResponse {
  photos: Photo[];
  year: string;
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE_OPTIONS = [24, 48, 96];
const DEFAULT_PAGE_SIZE = 24;

/** Page numbers to display, with ellipses for gaps. 1-indexed. */
function paginationItems(current: number, last: number): (number | 'gap')[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const candidates = new Set<number>([1, last, current, current - 1, current + 1]);
  const valid = Array.from(candidates).filter(n => n >= 1 && n <= last).sort((a, b) => a - b);
  const result: (number | 'gap')[] = [];
  for (let i = 0; i < valid.length; i++) {
    if (i > 0 && valid[i] - valid[i - 1] > 1) result.push('gap');
    result.push(valid[i]);
  }
  return result;
}

/** Best display URL for a photo. Falls back: medium → full → thumb. */
function lightboxSrc(photo: Photo): string {
  return photo.mediumUrl ?? photo.url ?? photo.thumbnailUrl ?? '';
}

export default function Gallery() {
  const { isSignedIn, signIn, getApiToken } = useAuth();
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);                            // 0-indexed
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [year, setYear] = useState('2026');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const [navigating, setNavigating] = useState(false);

  const totalPages   = Math.max(1, Math.ceil(total / pageSize));
  const lightboxIdx  = useMemo(
    () => (lightbox ? photos.findIndex(p => p.id === lightbox.id) : -1),
    [lightbox, photos]
  );

  // Year list (deduped defensively)
  useEffect(() => {
    if (!isSignedIn) return;
    fetch('/api/public/years')
      .then(r => r.json())
      .then(d => {
        const years: string[] = Array.isArray(d.years) ? d.years : [];
        const unique = Array.from(new Set(years)).sort().reverse();
        if (unique.length) setYearOptions(unique);
      })
      .catch(() => setYearOptions(['2026']));
  }, [isSignedIn]);

  // Reset to page 0 when year/tag/pageSize changes
  useEffect(() => { setPage(0); }, [year, activeTag, pageSize]);

  // Fetch a page
  useEffect(() => {
    if (!isSignedIn) return;
    setLoading(true);
    const url = `/api/public/photos?year=${year}&page=${page}&pageSize=${pageSize}` +
      (activeTag ? `&tag=${encodeURIComponent(activeTag)}` : '');
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data: PhotosResponse = await res.json();
        setTotal(data.total ?? 0);
        setPhotos(data.photos ?? []);
        // Scroll to top when pagination changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [year, activeTag, page, pageSize, isSignedIn, getApiToken]);

  // Eager-preload neighbors whenever the lightbox photo changes — keeps ‹/›
  // navigation instant. Browser caches the requests, so visiting the same
  // neighbor a second time is free.
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const preload = (idx: number) => {
      if (idx < 0 || idx >= photos.length) return;
      const src = lightboxSrc(photos[idx]);
      if (!src) return;
      const img = new Image();
      img.src = src;
    };
    preload(lightboxIdx - 1);
    preload(lightboxIdx + 1);
  }, [lightboxIdx, photos]);

  // Navigate to a sibling photo. Crosses page boundaries by fetching the
  // adjacent page; the lightbox feels like a single continuous stream
  // even though pagination is preserved.
  async function navigateLightbox(direction: -1 | 1) {
    if (!lightbox || navigating) return;

    const nextIdxOnPage = lightboxIdx + direction;

    // In-page navigation: just swap the lightbox photo
    if (nextIdxOnPage >= 0 && nextIdxOnPage < photos.length) {
      setLightbox(photos[nextIdxOnPage]);
      return;
    }

    // Cross-page navigation
    const targetPage = direction === 1 ? page + 1 : page - 1;
    if (targetPage < 0 || targetPage >= totalPages) return;

    setNavigating(true);
    try {
      const token = await getApiToken();
      const url = `/api/public/photos?year=${year}&page=${targetPage}&pageSize=${pageSize}` +
        (activeTag ? `&tag=${encodeURIComponent(activeTag)}` : '');
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const data: PhotosResponse = await res.json();
      const newPhotos = data.photos ?? [];
      if (newPhotos.length === 0) { setNavigating(false); return; }

      setPhotos(newPhotos);
      setPage(targetPage);
      // Forward → first photo on new page; Backward → last photo on new page
      setLightbox(direction === 1 ? newPhotos[0] : newPhotos[newPhotos.length - 1]);
    } catch {
      // Stay on current photo on failure
    } finally {
      setNavigating(false);
    }
  }

  // Keyboard: Esc closes, arrows navigate (with cross-page support)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!lightbox) return;
      if (e.key === 'Escape')   { setLightbox(null); return; }
      if (e.key === 'ArrowLeft')  navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, lightboxIdx, photos, page, totalPages, navigating]);

  // Tag chips — exclude tags that match a year (year tags are redundant with year tabs)
  const allTags = Array.from(new Set(photos.flatMap(p => p.tags)))
    .filter(tag => !yearOptions.includes(tag))
    .sort();

  // ── Sign-in gate ───────────────────────────────────────────────────────────
  if (!isSignedIn) {
    return (
      <section className="section">
        <div className="container gated">
          <span className="eyebrow">— Photo Gallery</span>
          <h1>Sign in to view photos</h1>
          <p>The WICnic photo gallery is for WIC employees only. Sign in with your Microsoft account to view past and current event photos.</p>
          <button type="button" onClick={signIn} className="btn-signin">Sign in with Microsoft</button>
          <Link to="/" className="btn-back">Back to home</Link>
          <style>{`
            .gated { max-width: 640px; padding: 4rem 0; }
            .gated h1 { font-family: var(--font-display); font-weight: 700; text-transform: uppercase; margin: 0.5rem 0 1rem; }
            .gated p { color: var(--steel); margin-bottom: 2rem; font-size: 1.05rem; }
            .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); }
            .btn-signin {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--white); background: var(--red); padding: 0.85rem 2rem;
              text-decoration: none; border: none; cursor: pointer; margin-right: 1rem;
            }
            .btn-back {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--steel); background: transparent; padding: 0.85rem 2rem;
              text-decoration: none; border: 1px solid var(--border);
            }
          `}</style>
        </div>
      </section>
    );
  }

  const firstOfPage = total === 0 ? 0 : page * pageSize + 1;
  const lastOfPage  = Math.min((page + 1) * pageSize, total);

  return (
    <>
      <style>{`
        .gallery-page { min-height: 100vh; background: var(--bg); }
        .gallery-hero {
          background: var(--black); color: var(--white);
          padding: 4rem 4rem 3rem; position: relative; overflow: hidden;
        }
        .gallery-hero::before {
          content: ''; position: absolute; inset: 0;
          background: repeating-linear-gradient(
            45deg, transparent, transparent 60px,
            rgba(192,57,43,0.05) 60px, rgba(192,57,43,0.05) 61px
          );
        }
        .gallery-hero-inner { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; }
        .eyebrow {
          font-family: var(--font-display); font-size: 0.75rem; font-weight: 500;
          letter-spacing: 0.35em; text-transform: uppercase; color: var(--red);
          margin-bottom: 0.75rem;
        }
        .gallery-hero h1 {
          font-family: var(--font-display); font-size: clamp(2.2rem, 4.5vw, 3.4rem);
          font-weight: 700; text-transform: uppercase; margin: 0;
        }
        .controls-bar {
          max-width: 1200px; margin: 0 auto;
          padding: 2rem 4rem 0;
          display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap;
        }
        @media (max-width: 768px) {
          .controls-bar { padding: 1.5rem 1.5rem 0; }
          .gallery-hero { padding: 3rem 1.5rem 2rem; }
        }
        .year-tabs { display: flex; gap: 0; }
        .year-tab {
          font-family: var(--font-display); font-size: 0.8rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.05em;
          padding: 0.5rem 1rem; border: 1px solid var(--border); background: var(--white);
          color: var(--steel); cursor: pointer; margin-left: -1px; transition: all 0.15s;
        }
        .year-tab.active { background: var(--red); border-color: var(--red); color: var(--white); z-index: 1; position: relative; }
        .year-tab:hover:not(.active) { background: var(--bg); }
        .tag-filters { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .tag-chip {
          font-family: var(--font-body); font-size: 0.8rem;
          padding: 0.3rem 0.75rem; border: 1px solid var(--border);
          background: var(--white); color: var(--mid); cursor: pointer;
          border-radius: 0; transition: all 0.15s;
        }
        .tag-chip.active { background: var(--black); border-color: var(--black); color: var(--white); }
        .tag-chip:hover:not(.active) { border-color: var(--mid); }
        .page-size-control {
          margin-left: auto; display: flex; align-items: center; gap: 0.5rem;
          font-family: var(--font-body); font-size: 0.85rem; color: var(--mid);
        }
        .page-size-control select {
          font-family: var(--font-body); font-size: 0.85rem;
          padding: 0.3rem 0.5rem; border: 1px solid var(--border);
          background: var(--white); color: var(--steel); cursor: pointer;
        }
        .gallery-grid-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 4rem 5rem; }
        @media (max-width: 768px) { .gallery-grid-wrap { padding: 1.5rem; } }
        .photo-count { font-family: var(--font-body); font-size: 0.9rem; color: var(--steel); margin-bottom: 1.5rem; }
        .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 2px; background: var(--border); }
        .photo-card { background: var(--white); cursor: pointer; overflow: hidden; position: relative; aspect-ratio: 4/3; }
        .photo-card img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.3s ease; }
        .photo-card:hover img { transform: scale(1.04); }
        .photo-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%);
          opacity: 0; transition: opacity 0.2s;
          display: flex; align-items: flex-end; padding: 1rem;
        }
        .photo-card:hover .photo-overlay { opacity: 1; }
        .photo-caption-hover { font-family: var(--font-body); font-size: 0.85rem; color: var(--white); line-height: 1.4; }
        .state-box { padding: 4rem 2rem; text-align: center; font-family: var(--font-body); color: var(--muted); }
        .state-box h2 { font-family: var(--font-display); font-size: 1.3rem; font-weight: 600; text-transform: uppercase; color: var(--mid); margin-bottom: 0.5rem; }

        /* Pagination */
        .pagination {
          display: flex; align-items: center; justify-content: center; gap: 0.25rem;
          margin-top: 3rem; flex-wrap: wrap;
        }
        .page-btn {
          font-family: var(--font-display); font-size: 0.8rem; font-weight: 600;
          min-width: 2.5rem; height: 2.5rem; padding: 0 0.75rem;
          border: 1px solid var(--border); background: var(--white); color: var(--steel);
          cursor: pointer; transition: all 0.15s;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .page-btn:hover:not(:disabled):not(.active) { border-color: var(--red); color: var(--red); }
        .page-btn.active { background: var(--red); border-color: var(--red); color: var(--white); cursor: default; }
        .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .page-gap { font-family: var(--font-body); color: var(--muted); padding: 0 0.4rem; user-select: none; }

        /* Lightbox */
        .lightbox-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.92); z-index: 1000;
          display: flex; align-items: center; justify-content: center; padding: 4rem 2rem;
        }
        .lightbox-inner { max-width: 90vw; max-height: 90vh; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .lightbox-inner img { max-width: 100%; max-height: 75vh; object-fit: contain; display: block; }
        .lightbox-caption { font-family: var(--font-body); font-size: 0.9rem; color: rgba(255,255,255,0.8); text-align: center; max-width: 600px; }
        .lightbox-credit { font-family: var(--font-body); font-size: 0.75rem; color: rgba(255,255,255,0.4); }
        .lightbox-counter {
          position: fixed; top: 1.5rem; left: 1.5rem;
          font-family: var(--font-display); font-size: 0.75rem; font-weight: 600;
          letter-spacing: 0.1em; text-transform: uppercase;
          color: rgba(255,255,255,0.6);
          padding: 0.5rem 1rem; border: 1px solid rgba(255,255,255,0.15);
        }
        .lightbox-toolbar {
          position: fixed; top: 1.5rem; right: 1.5rem;
          display: flex; gap: 0.5rem; align-items: center;
        }
        .lightbox-fullsize {
          background: rgba(255,255,255,0.92); color: var(--black);
          font-family: var(--font-display); font-size: 0.75rem; font-weight: 600;
          letter-spacing: 0.1em; text-transform: uppercase;
          padding: 0.5rem 1rem; cursor: pointer; text-decoration: none;
          border: 1px solid rgba(255,255,255,0.92);
          transition: all 0.15s;
        }
        .lightbox-fullsize:hover { background: var(--red); color: var(--white); border-color: var(--red); }
        .lightbox-close {
          background: none; border: 1px solid rgba(255,255,255,0.3); color: var(--white);
          font-family: var(--font-display); font-size: 0.75rem; font-weight: 600;
          letter-spacing: 0.1em; text-transform: uppercase;
          padding: 0.5rem 1rem; cursor: pointer;
        }
        .lightbox-close:hover { background: rgba(255,255,255,0.1); }
        .lightbox-nav {
          position: fixed; top: 50%; transform: translateY(-50%);
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2);
          color: var(--white); width: 3.5rem; height: 3.5rem;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.5rem; cursor: pointer; transition: background 0.15s;
        }
        .lightbox-nav:hover:not(:disabled) { background: rgba(255,255,255,0.18); }
        .lightbox-nav:disabled { opacity: 0.25; cursor: not-allowed; }
        .lightbox-nav.prev { left: 1.5rem; }
        .lightbox-nav.next { right: 1.5rem; }
        @media (max-width: 768px) {
          .lightbox-nav { width: 2.5rem; height: 2.5rem; }
          .lightbox-nav.prev { left: 0.5rem; }
          .lightbox-nav.next { right: 0.5rem; }
          .lightbox-toolbar { top: 0.75rem; right: 0.75rem; }
          .lightbox-fullsize, .lightbox-close { padding: 0.4rem 0.7rem; font-size: 0.65rem; }
        }
      `}</style>

      <div className="gallery-page">
        <div className="gallery-hero">
          <div className="gallery-hero-inner">
            <p className="eyebrow">— Photo Gallery</p>
            <h1>WICnic Memories</h1>
          </div>
        </div>

        <PhotoUpload defaultYear={year} availableYears={yearOptions} />

        <div className="controls-bar">
          <div className="year-tabs">
            {yearOptions.map((y) => (
              <button
                key={y}
                className={`year-tab${year === y ? ' active' : ''}`}
                onClick={() => { setYear(y); setActiveTag(null); }}
              >
                {y}
              </button>
            ))}
          </div>

          {allTags.length > 0 && (
            <div className="tag-filters">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  className={`tag-chip${activeTag === tag ? ' active' : ''}`}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          <div className="page-size-control">
            <label htmlFor="page-size">Per page:</label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <div className="gallery-grid-wrap">
          {loading && <div className="state-box"><h2>Loading photos…</h2></div>}

          {!loading && photos.length === 0 && (
            <div className="state-box">
              <h2>No photos yet</h2>
              <p>
                {year === '2026'
                  ? 'Photos will be added after the event. Check back soon!'
                  : 'No photos found for this year.'}
              </p>
            </div>
          )}

          {!loading && photos.length > 0 && (
            <>
              <p className="photo-count">
                Showing {firstOfPage}–{lastOfPage} of {total} photo{total !== 1 ? 's' : ''}
                {activeTag ? ` tagged "${activeTag}"` : ''}
              </p>
              <div className="gallery-grid">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    className="photo-card"
                    onClick={() => setLightbox(photo)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setLightbox(photo)}
                    aria-label={photo.caption ?? 'WICnic photo'}
                  >
                    <img src={photo.thumbnailUrl ?? photo.url} alt={photo.caption ?? 'WICnic photo'} loading="lazy" decoding="async" />
                    {photo.caption && (
                      <div className="photo-overlay">
                        <span className="photo-caption-hover">{photo.caption}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination" role="navigation" aria-label="Gallery pagination">
                  <button
                    className="page-btn"
                    disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    aria-label="Previous page"
                  >
                    ‹ Prev
                  </button>
                  {paginationItems(page + 1, totalPages).map((item, i) =>
                    item === 'gap' ? (
                      <span key={`gap-${i}`} className="page-gap">…</span>
                    ) : (
                      <button
                        key={item}
                        className={`page-btn${item === page + 1 ? ' active' : ''}`}
                        onClick={() => setPage(item - 1)}
                        aria-label={`Page ${item}`}
                        aria-current={item === page + 1 ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    className="page-btn"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    aria-label="Next page"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {lightbox && (
        <div
          className="lightbox-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
          role="dialog"
          aria-modal="true"
          aria-label="Photo lightbox"
        >
          <span className="lightbox-counter">{page * pageSize + lightboxIdx + 1} / {total}</span>
          <div className="lightbox-toolbar">
            <a
              className="lightbox-fullsize"
              href={lightbox.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              View Full Size
            </a>
            <button className="lightbox-close" onClick={() => setLightbox(null)}>Close ✕</button>
          </div>

          <button
            className="lightbox-nav prev"
            disabled={navigating || (lightboxIdx === 0 && page === 0)}
            onClick={() => navigateLightbox(-1)}
            aria-label="Previous photo"
          >{navigating ? '…' : '‹'}</button>

          <div className="lightbox-inner">
            <img src={lightboxSrc(lightbox)} alt={lightbox.caption ?? 'WICnic photo'} />
            {lightbox.caption && <p className="lightbox-caption">{lightbox.caption}</p>}
            {lightbox.credit && <p className="lightbox-credit">Photo: {lightbox.credit}</p>}
          </div>

          <button
            className="lightbox-nav next"
            disabled={navigating || (lightboxIdx === photos.length - 1 && page >= totalPages - 1)}
            onClick={() => navigateLightbox(1)}
            aria-label="Next photo"
          >{navigating ? '…' : '›'}</button>
        </div>
      )}
    </>
  );
}